import {
  OverrideProperty,
  OverridePropertyHelper,
} from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdRelativeDateIntervalCondition,
  DateRangeCondition,
} from '@bindoc-projects/condition-domain-lib';
import type {
  BdIntervalRelative,
  BdIntervalRelativePart,
} from '@bindoc-projects/time-domain-lib';
import {
  BdDateInterval,
  BdTimeSelection,
  BdTimeSelectionCalendric,
  BdTimeSelectionRelative,
} from '@bindoc-projects/time-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';

export const BD_ADMISSION_DATE_CALENDRIC_CONDITION_ID =
  'calendricAdmissionDate';
export const BD_ADMISSION_DATE_RELATIVE_CONDITION_ID = 'relativeAdmissionDate';
export const FilterConditionAdmissionDatePluginType: FilterConditionPluginSpecification<
  BdTimeSelection,
  JsonObj,
  | {
      start: BdIntervalRelativePart;
      duration: BdIntervalRelativePart;
    }
  | FilterConditionCalendricAdmissionDateCondition
> = {
  type: 'admissionDate',
  category: 'case',
  displayName: { de: 'Aufnahmedatum' },
  sortingIndex: 8,
  occurrence: 'multiple',
  fromObject(obj: JsonObj): FilterConditionPluginImpl<BdTimeSelection> {
    return new FilterConditionAdmissionDatePlugin(
      BdTimeSelection.fromObject(
        obj.timeSelection as
          | {
              type: 'calendric';
              interval: unknown;
            }
          | {
              type: 'relative';
              intervalRelative: {
                start: BdIntervalRelativePart;
                duration: BdIntervalRelativePart;
              };
            },
      ),
    );
  },
  fromCondition(
    jsonObj:
      | {
          start: BdIntervalRelativePart;
          duration: BdIntervalRelativePart;
        }
      | FilterConditionCalendricAdmissionDateCondition,
  ): FilterConditionPluginImpl<BdTimeSelection> {
    if (Object.hasOwn(jsonObj, 'from')) {
      return new FilterConditionAdmissionDatePlugin(
        new BdTimeSelectionCalendric(
          new BdDateInterval(
            new Date(
              (jsonObj as FilterConditionCalendricAdmissionDateCondition).from,
            ),
            new Date(
              (jsonObj as FilterConditionCalendricAdmissionDateCondition).to,
            ),
          ),
        ),
      );
    } else {
      return new FilterConditionAdmissionDatePlugin(
        BdTimeSelectionRelative.fromObject({
          type: 'relative',
          intervalRelative: jsonObj as {
            start: BdIntervalRelativePart;
            duration: BdIntervalRelativePart;
          },
        }),
      );
    }
  },
  newInstance(): FilterConditionPluginImpl<BdTimeSelection> {
    return new FilterConditionAdmissionDatePlugin(
      new BdTimeSelectionCalendric(new BdDateInterval(new Date(), new Date())),
    );
  },
};

export class FilterConditionAdmissionDatePlugin extends FilterConditionPluginImpl<
  BdTimeSelection,
  JsonObj
> {
  static override Specification: FilterConditionPluginSpecification<
    BdTimeSelection,
    JsonObj,
    | {
        start: BdIntervalRelativePart;
        duration: BdIntervalRelativePart;
      }
    | FilterConditionCalendricAdmissionDateCondition
  > = FilterConditionAdmissionDatePluginType;

  constructor(private timeSelection: BdTimeSelection) {
    super(FilterConditionAdmissionDatePluginType.type);
  }

  override hasRelativeTimeSelection(): boolean {
    return this.timeSelection.type === 'relative';
  }

  toCondition(): BdCondition {
    switch (this.timeSelection.type) {
      case 'relative': {
        const relativeTimeSelection = this
          .timeSelection as BdTimeSelectionRelative;
        return new FilterConditionRelativeAdmissionDateCondition(
          relativeTimeSelection.intervalRelative,
        );
      }
      default: {
        const calendricTimeSelection = this
          .timeSelection as BdTimeSelectionCalendric;
        return new FilterConditionCalendricAdmissionDateCondition(
          calendricTimeSelection.interval,
        );
      }
    }
  }

  toJSON(): JsonObj | null {
    return {
      type: this.type,
      timeSelection: this.timeSelection.toJSON(),
    };
  }

  override toDetailedText(): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const items: string[] = [];
    switch (this.timeSelection.type) {
      case 'calendric': {
        const calendric = this.timeSelection as BdTimeSelectionCalendric;
        items.push(calendric.interval.toCompactDateString());
        break;
      }
      case 'relative': {
        const relative = this.timeSelection as BdTimeSelectionRelative;
        items.push(relative.intervalRelative.toDisplayString());
        break;
      }
    }
    return { title, items };
  }

  toText(): string | null {
    return 'Aufnahmedatum';
  }

  toValue(): BdTimeSelection {
    return this.timeSelection;
  }

  override getOverrideProperties(
    tileId: string,
  ): OverrideProperty[] {
    return [
      new OverrideProperty<BdTimeSelection>(
        tileId,
        'admissionDate',
        this.timeSelection,
        this.timeSelection,
        'possible',
        undefined,
        (a, b) => a.equals(b),
        (cloneValue) => cloneValue.clone(),
      ),
    ];
  }

  override overrideClone(
    override: OverrideProperty[],
  ): FilterConditionAdmissionDatePlugin {
    const _property = OverridePropertyHelper.overrideProperty<BdTimeSelection>(
      this.timeSelection,
      'admissionDate',
      override,
      (value) => value.clone(),
    );

    if (_property) {
      return new FilterConditionAdmissionDatePlugin(_property);
    } else {
      return null;
    }
  }

  override equals(other: FilterConditionAdmissionDatePlugin): boolean {
    return (
      super.equals(other) && this.timeSelection.equals(other.timeSelection)
    );
  }

  override valid(): boolean {
    return super.valid() && this.timeSelection.valid();
  }

  override validationToText(): string {
    if (!this.timeSelection || !this.timeSelection.valid()) {
      return 'Einen gültigen Zeitraum auswählen';
    } else {
      return '';
    }
  }

  clone(): FilterConditionAdmissionDatePlugin {
    return new FilterConditionAdmissionDatePlugin(this.timeSelection.clone());
  }
}

export class FilterConditionCalendricAdmissionDateCondition extends DateRangeCondition {
  constructor(interval: BdDateInterval) {
    super(BD_ADMISSION_DATE_CALENDRIC_CONDITION_ID, interval);
  }
}

export class FilterConditionRelativeAdmissionDateCondition extends BdRelativeDateIntervalCondition {
  constructor(intervalRelative: BdIntervalRelative) {
    super(BD_ADMISSION_DATE_RELATIVE_CONDITION_ID, intervalRelative);
  }
}

interface JsonObj {
  type: string;
  timeSelection: unknown;
}
